import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useGetAllBatchIdsQuery } from 'common/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useBatchId from './discountModalData/useBatchId';
import useCoupon from './discountModalData/useCoupon';
import useNumberCoupons from './discountModalData/useNumberCoupons';
import usePercentage from './discountModalData/usePercentage';
import Grid from '@mui/material/Grid2';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useExpiryDate } from './discountModalData/useExpiryDate';
import { CreateTrialDiscountBody } from './types';

export default function CreateDiscounModal(props: Props) {
  const [discountMode, setDiscountMode] = useState<'standard' | 'trial'>(
    'standard'
  );

  const [unlimited, setUnlimited] = useState(false);
  const [repeteable, setRepetable] = useState(false);
  const [expiresAfterDate, setExpiresAfterDate] = useState(false);
  const [disablesUsersAfterExpiry, setDisablesUsersAfterExpiry] =
    useState(false);

  const [membershipType, setMembershipType] = useState<MembershipType>(
    'plan_without_genetics'
  );

  const [trialDuration, setTrialDuration] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<'gbp' | 'usd' | 'eur' | 'jpy'>(
    'gbp'
  );
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [trialDurationError, setTrialDurationError] = useState('');
  const [priceError, setPriceError] = useState('');
  const [expiryType, setExpiryType] = useState<'duration' | 'fixedDate'>(
    'duration'
  );

  const { data: batchIds = [] } = useGetAllBatchIdsQuery();

  const { onCreatePressed, apiCouponError, onCreateTrial } = props;

  const {
    percentageValue,
    percentage,
    onPercentageBlur,
    onPercentageChangeText,
    percetageError,
    onPercentageFocus,
    isPercentageValid,
  } = usePercentage();

  const {
    batchId,
    batchIdError,
    onBatchIdFocus,
    onBatchIdChange,
    isBatchIdValid,
  } = useBatchId();

  const {
    numberCoupons,
    numberCouponsError,
    onNumberCouponsFocus,
    onNumberCouponsChange,
    onNumberCouponsBlur,

    setNumberCoupons,
    setNumberCouponsError,
    isNumberOfCouponsValid,

    numberOfUses,
    numberOfUsesError,
    onNumberUsesFocus,
    onNumberUsesChange,
    onNumberUsesBlur,

    setNumberOfUses,
    setNumberOfUsesError,
    isNumberOfUsesValid,
  } = useNumberCoupons();

  const {
    onCouponFocus,
    isCouponValid,

    coupon,
    setCoupon,
    couponError,
    setCouponError,
  } = useCoupon();

  const {
    expiryDate,
    expiryDateError,
    isExpiryValid,
    onExpiryDateBlur,
    onExpiryDateFocus,
    setExpiryDate,
    onExpiryChange,
  } = useExpiryDate();

  const disableButton = useMemo(() => {
    const commonTrialErrors =
      batchIdError.length > 0 ||
      (unlimited && couponError.length > 0) ||
      (!unlimited && numberCouponsError.length > 0);

    if (discountMode === 'standard') {
      return (
        commonTrialErrors ||
        percetageError.length > 0 ||
        (expiresAfterDate && expiryDateError.length > 0)
      );
    } else {
      let expiryMethodError = false;
      if (expiryType === 'duration') {
        expiryMethodError = trialDurationError.length > 0;
      } else {
        expiryMethodError = expiryDateError.length > 0;
      }

      return commonTrialErrors || expiryMethodError;
    }
  }, [
    batchIdError.length,
    unlimited,
    couponError.length,
    numberCouponsError.length,
    discountMode,
    percetageError.length,
    expiresAfterDate,
    expiryDateError.length,
    expiryType,
    trialDurationError.length,
  ]);

  const onCreateStandardButtonPressed = useCallback(() => {
    const batchIdValid = isBatchIdValid();
    const percentageValid = isPercentageValid();
    const couponValid = unlimited || repeteable ? isCouponValid() : true;
    const numberOfCouponsValid = !unlimited ? isNumberOfCouponsValid() : true;
    const numberOfUsesValid = repeteable ? isNumberOfUsesValid() : true;
    const expiryValid = expiresAfterDate ? isExpiryValid() : true;

    if (
      batchIdValid &&
      percentageValid &&
      couponValid &&
      numberOfCouponsValid &&
      numberOfUsesValid &&
      expiryValid
    ) {
      onCreatePressed(
        batchId,
        membershipType,
        numberCoupons,
        percentageValue,
        unlimited,
        unlimited || repeteable ? coupon : '',
        repeteable,
        numberOfUses,
        expiresAfterDate ? expiryDate?.format('DD-MM-YYYY') ?? null : null,
        disablesUsersAfterExpiry
      );
    }
  }, [
    batchId,
    coupon,
    disablesUsersAfterExpiry,
    expiresAfterDate,
    expiryDate,
    isBatchIdValid,
    isCouponValid,
    isExpiryValid,
    isNumberOfCouponsValid,
    isNumberOfUsesValid,
    isPercentageValid,
    membershipType,
    numberCoupons,
    numberOfUses,
    onCreatePressed,
    percentageValue,
    repeteable,
    unlimited,
  ]);

  const onCreateTrialButtonPressed = useCallback(() => {
    const isBatchValid = isBatchIdValid();
    const isCouponCodeValid = unlimited || repeteable ? isCouponValid() : true;
    const isPriceValid = price.trim() !== '' && parseFloat(price) > 0;
    const isUsesValid = !unlimited ? isNumberOfUsesValid() : true;

    setPriceError(isPriceValid ? '' : 'Must be a positive number');

    let isExpiryMethodValid = false;
    let payload: CreateTrialDiscountBody | null = null;

    if (expiryType === 'duration') {
      const isTrialDurationValid =
        trialDuration.trim() !== '' && parseInt(trialDuration, 10) > 0;
      setTrialDurationError(
        isTrialDurationValid ? '' : 'Must be a positive number'
      );
      isExpiryMethodValid = isTrialDurationValid;

      if (isExpiryMethodValid) {
        payload = {
          batchId,
          couponCode: coupon,
          unlimited,
          numberOfUses: unlimited ? null : parseInt(numberOfUses, 10),
          pricePennies: Math.round(parseFloat(price) * 100),
          currency,
          interval,
          trialDurationDays: parseInt(trialDuration, 10),
          expiryDate: null,
          numberOfCoupons: parseInt(numberCoupons, 10),
        };
      }
    } else {
      isExpiryMethodValid = isExpiryValid();

      if (isExpiryMethodValid) {
        payload = {
          batchId,
          couponCode: coupon,
          unlimited,
          numberOfUses: unlimited ? null : parseInt(numberOfUses, 10),
          pricePennies: Math.round(parseFloat(price) * 100),
          currency,
          interval,
          trialDurationDays: null,
          expiryDate: expiryDate?.format('YYYY-MM-DD') ?? null,
          numberOfCoupons: parseInt(numberCoupons, 10),
        };
      }
    }

    if (
      isBatchValid &&
      isCouponCodeValid &&
      isPriceValid &&
      isUsesValid &&
      isExpiryMethodValid &&
      payload
    ) {
      onCreateTrial(payload);
    }
  }, [
    isBatchIdValid,
    unlimited,
    repeteable,
    isCouponValid,
    price,
    isNumberOfUsesValid,
    expiryType,
    trialDuration,
    batchId,
    coupon,
    numberOfUses,
    currency,
    interval,
    numberCoupons,
    isExpiryValid,
    expiryDate,
    onCreateTrial,
  ]);

  useEffect(() => {
    if (discountMode === 'trial') {
      setExpiresAfterDate(expiryType === 'fixedDate');
    }
  }, [discountMode, expiryType, setExpiresAfterDate]);

  useEffect(() => {
    if (apiCouponError) {
      setCouponError(apiCouponError);
    }
  }, [apiCouponError, setCouponError]);

  const clearTextFields = useCallback(() => {
    setNumberOfUsesError('');
    setNumberOfUses('1');
    setNumberCouponsError('');
    setNumberCoupons('1');
  }, [
    setNumberCoupons,
    setNumberCouponsError,
    setNumberOfUses,
    setNumberOfUsesError,
  ]);

  return (
    <div className="topModalContainer">
      <div className="discountCoupons createDiscountModal">
        <Typography variant="h6" gutterBottom>
          Create Discount Coupon
        </Typography>

        {/* --- 1. The Mode Toggle --- */}
        <ToggleButtonGroup
          color="primary"
          value={discountMode}
          exclusive
          onChange={(_e, newMode) => {
            if (newMode) {
              setDiscountMode(newMode);
            }
          }}
          aria-label="Discount Creation Mode"
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton value="standard">Standard Discount</ToggleButton>
          <ToggleButton value="trial">Trial Subscription</ToggleButton>
        </ToggleButtonGroup>

        {/* --- 2. Shared Batch ID Field --- */}
        {/* This field is used by both discount types. */}
        <Autocomplete
          options={batchIds}
          freeSolo={true}
          onChange={(_event, newValue) => {
            onBatchIdChange(newValue ?? '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Batch ID"
              className="textField"
              variant="standard"
              type="text"
              onChange={({ target: { value } }) => onBatchIdChange(value)}
              onFocus={onBatchIdFocus}
              error={batchIdError.length > 0}
              helperText={batchIdError}
              onBlur={isBatchIdValid}
            />
          )}
        />

        {(unlimited || repeteable) && (
          <TextField
            label="Coupon"
            className="textField marginTop20"
            variant="standard"
            type="text"
            onChange={({ target: { value } }) => setCoupon(value)}
            onFocus={onCouponFocus}
            error={couponError.length > 0}
            helperText={couponError}
            onBlur={isCouponValid}
          />
        )}

        {/* --- 3. Conditional Rendering of the Main Form --- */}
        {discountMode === 'standard' ? (
          // --- A. STANDARD DISCOUNT FORM (Your Original UI) ---
          <>
            <TextField
              label="Percentage"
              className="textField marginTop20"
              type="number"
              variant="standard"
              value={percentage}
              onBlur={onPercentageBlur}
              onFocus={onPercentageFocus}
              onChange={({ target: { value } }) =>
                onPercentageChangeText(value)
              }
              error={percetageError.length > 0}
              helperText={percetageError}
            />

            <FormControl className="marginTop40" fullWidth>
              <InputLabel id="discountTypeLabel">Discount type</InputLabel>
              <Select
                label="Discount type"
                labelId="discountTypeLabel"
                value={membershipType}
                onChange={({ target: { value } }) =>
                  setMembershipType(value as MembershipType)
                }
              >
                <MenuItem value="plan">Expert</MenuItem>
                <MenuItem value="plan_without_genetics">Discovery</MenuItem>
                <MenuItem value="membership_upgrade">Expert upgrade</MenuItem>
              </Select>
            </FormControl>
          </>
        ) : (
          // --- B. TRIAL SUBSCRIPTION FORM (New UI) ---
          <>
            <FormControl component="fieldset" sx={{ mt: 3, mb: 1 }}>
              <FormLabel component="legend">Trial Expiry Method</FormLabel>
              <RadioGroup
                row
                value={expiryType}
                onChange={(e) =>
                  setExpiryType(e.target.value as 'duration' | 'fixedDate')
                }
              >
                <FormControlLabel
                  value="duration"
                  control={<Radio />}
                  label="By Duration"
                />
                <FormControlLabel
                  value="fixedDate"
                  control={<Radio />}
                  label="On a Fixed Date"
                />
              </RadioGroup>
            </FormControl>

            {unlimited && (
              <TextField
                label="Coupon Code"
                required
                className="textField marginTop20"
                variant="standard"
                value={coupon}
                onChange={({ target: { value } }) => setCoupon(value)}
                onFocus={onCouponFocus}
                error={couponError.length > 0}
                helperText={couponError}
                onBlur={isCouponValid}
              />
            )}
            {expiryType === 'duration' && (
              <TextField
                label="Trial Duration (days)"
                required
                className="textField marginTop20"
                type="number"
                variant="standard"
                value={trialDuration}
                onChange={({ target: { value } }) => {
                  setTrialDuration(value);
                  setTrialDurationError('');
                }}
                error={!!trialDurationError}
                helperText={trialDurationError}
              />
            )}

            <Grid
              container
              spacing={2}
              className="marginTop20"
              alignItems="flex-end"
            >
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Price After Trial"
                  required
                  fullWidth
                  type="number"
                  variant="standard"
                  value={price}
                  onChange={({ target: { value } }) => {
                    setPrice(value);
                    setPriceError('');
                  }}
                  error={!!priceError}
                  helperText={priceError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency === 'gbp'
                          ? '£'
                          : currency === 'usd'
                          ? '$'
                          : currency === 'eur'
                          ? '€'
                          : '¥'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 3 }}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value as any)}
                  >
                    <MenuItem value="gbp">GBP</MenuItem>
                    <MenuItem value="usd">USD</MenuItem>
                    <MenuItem value="eur">EUR</MenuItem>
                    <MenuItem value="jpy">YEN</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 3 }}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Interval</InputLabel>
                  <Select
                    value={interval}
                    onChange={(e) => setInterval(e.target.value as any)}
                  >
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="year">Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}

        {/* --- 4. Shared Form Elements --- */}
        {/* This section is common to both discount types */}

        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className="marginTop20"
        >
          <Grid size={{ sm: 3 }}>
            <TextField
              label="Number of coupons"
              className="textField"
              type="number"
              variant="standard"
              disabled={unlimited || repeteable}
              error={numberCouponsError.length > 0}
              helperText={numberCouponsError}
              onBlur={onNumberCouponsBlur}
              onFocus={onNumberCouponsFocus}
              onChange={({ target: { value } }) => onNumberCouponsChange(value)}
              value={numberCoupons}
            />
          </Grid>
          <Grid size={{ sm: 2 }} className="marginTop20">
            <FormControlLabel
              control={
                <Checkbox
                  checked={unlimited}
                  onChange={({ target: { checked } }) => {
                    clearTextFields();
                    setUnlimited(checked);
                    if (checked && repeteable) {
                      setRepetable(false);
                    }
                  }}
                />
              }
              label="Unlimited"
            />
          </Grid>
          <Grid size={{ sm: 3 }}>
            <TextField
              label="Number of uses"
              className="textField"
              type="number"
              variant="standard"
              disabled={!repeteable}
              error={numberOfUsesError.length > 0 && !unlimited}
              helperText={unlimited ? '' : numberOfUsesError}
              onBlur={onNumberUsesBlur}
              onFocus={onNumberUsesFocus}
              onChange={({ target: { value } }) => onNumberUsesChange(value)}
              value={numberOfUses}
            />
          </Grid>
          <Grid size={{ sm: 2 }} className="marginTop20">
            <FormControlLabel
              control={
                <Checkbox
                  checked={repeteable}
                  onChange={({ target: { checked } }) => {
                    clearTextFields();
                    setRepetable(checked);
                    if (checked && unlimited) {
                      setUnlimited(false);
                    }
                  }}
                />
              }
              label="Repeatable"
            />
          </Grid>
        </Grid>

        <Grid
          container
          direction={'row'}
          alignItems={'flex-end'}
          sx={{ marginTop: '20px' }}
        >
          <Grid size={{ xs: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    expiresAfterDate ||
                    (discountMode === 'trial' && expiryType === 'fixedDate')
                  }
                  disabled={discountMode === 'trial'}
                  sx={{ '&.MuiCheckbox-root': { padding: '0px 9px' } }}
                  onChange={({ target: { checked } }) => {
                    setExpiresAfterDate(checked);
                    if (!checked) {
                      setDisablesUsersAfterExpiry(false);
                      setExpiryDate(null);
                    }
                  }}
                />
              }
              label="Expires after"
            />
          </Grid>

          {/* This field only makes sense for the standard discount type */}
          {discountMode === 'standard' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={disablesUsersAfterExpiry}
                  sx={{ '&.MuiCheckbox-root': { padding: '0px 9px' } }}
                  onChange={({ target: { checked } }) => {
                    setDisablesUsersAfterExpiry(checked);
                  }}
                />
              }
              label="Disable users after expiry"
            />
          )}

          <Grid size={{ xs: 3 }} justifyContent={'center'} container flex={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={
                  discountMode === 'trial' && expiryType === 'fixedDate'
                    ? 'Trial Expiry Date'
                    : 'Coupon Expiry Date'
                }
                minDate={dayjs()}
                disabled={
                  !expiresAfterDate &&
                  !(discountMode === 'trial' && expiryType === 'fixedDate')
                }
                value={expiryDate}
                onChange={onExpiryChange}
                slotProps={{
                  textField: {
                    onBlur: onExpiryDateBlur,
                    onFocus: onExpiryDateFocus,
                    helperText: expiryDateError,
                    error: expiryDateError.length > 0,
                    variant: 'standard',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        {/* --- 5. The Final Create Button --- */}
        <Grid justifyContent="flex-end" container>
          <Button
            variant="contained"
            className="marginTop40 marginRight20"
            onClick={
              discountMode === 'standard'
                ? onCreateStandardButtonPressed
                : onCreateTrialButtonPressed
            }
            disabled={disableButton}
          >
            Create
          </Button>
        </Grid>
      </div>
    </div>
  );
}

type MembershipType = 'plan' | 'plan_without_genetics' | 'membership_upgrade';

interface Props {
  onCreatePressed: (
    batchId: string,
    discountType: string,
    numberOfCoupons: string,
    percentValue: number,
    unlimited: boolean,
    coupon: string,
    repeatable: boolean,
    numberOfUses: string,
    expiryDate: string | null,
    disableUsersAfterExpiry: boolean
  ) => void;

  onCreateTrial: (payload: CreateTrialDiscountBody) => void;

  apiCouponError?: string;
}
